import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { gsap } from "./lib/anim";
import { Icon } from "./components/Icons";
import { Plate } from "./components/ui";
import { STUDIO } from "./lib/store";
import { login, signup, loginWithCode } from "./lib/auth";
import { apiReadInvite } from "./lib/authApi";
import { ROLES, type RoleKey } from "./lib/permissions";

const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.key, r.label])) as Record<string, string>;

type Mode = "studio" | "client";

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.get("role") === "client" ? "client" : "studio");
  const [signUp, setSignUp] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", code: "" });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const inviteToken = params.get("invite") ?? "";
  const [invite, setInvite] = useState<{ role: RoleKey; email: string; name: string } | null>(null);
  const [inviteDead, setInviteDead] = useState(false);

  // An invitation opens straight into sign-up, prefilled and role-stamped.
  useEffect(() => {
    if (!inviteToken) return;
    let live = true;
    (async () => {
      const inv = await apiReadInvite(inviteToken);
      if (!live) return;
      if (!inv) { setInviteDead(true); return; }
      setInvite({ role: inv.role, email: inv.email, name: inv.name });
      setSignUp(true); setMode("studio");
      setForm((f) => ({ ...f, email: inv.email || f.email, name: inv.name || f.name }));
    })();
    return () => { live = false; };
  }, [inviteToken]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = gsap.context(() => { gsap.from(".lg-el", { y: 16, opacity: 0, duration: 0.6, ease: "power3.out", stagger: 0.07 }); }, root);
    return () => ctx.revert();
  }, [mode, signUp]);

  async function submitStudio(e?: React.FormEvent) {
    e?.preventDefault();
    setErr(null); setBusy(true);
    try {
      if (signUp) await signup(form.email, form.password, form.name, inviteToken || undefined);
      else await login(form.email, form.password);
      nav("/studio/projects");
    } catch (e: any) { setErr(e.message || "Something went wrong."); shake(); }
    finally { setBusy(false); }
  }
  async function submitClient(e?: React.FormEvent) {
    e?.preventDefault();
    if (!form.code.trim()) return;
    setErr(null); setBusy(true);
    try {
      const s = await loginWithCode(form.code);
      if (!s) { setErr("That access code isn't recognised. Check it against your invitation — it's not case-sensitive."); shake(); return; }
      nav(`/portal/${s.projectId}`);
    } finally { setBusy(false); }
  }
  function shake() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.fromTo(".lg-card", { x: -8 }, { x: 0, duration: 0.5, ease: "elastic.out(1,0.35)" });
  }

  return (
    <div ref={root} style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "clamp(24px,6vw,72px)", position: "relative", background: "var(--paper)" }}>
      <div style={{ position: "absolute", top: "-14%", right: "-10%", width: 460, height: 460, opacity: 0.4, filter: "blur(3px)", pointerEvents: "none" }}>
        <div style={{ borderRadius: "50%", overflow: "hidden", width: "100%", height: "100%" }}><Plate hue={26} h={460} seed={4} /></div>
      </div>

      <div className="lg-card card" style={{ width: "100%", maxWidth: 420, padding: 34, zIndex: 1, boxShadow: "var(--shadow-lg)" }}>
        <div className="lg-el row" style={{ gap: 12, marginBottom: 22 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center" }}><Icon.logo width={22} /></div>
          <div><div className="serif" style={{ fontSize: 20, lineHeight: 1 }}>{STUDIO.name}</div><div className="kicker" style={{ fontSize: 9.5, marginTop: 3 }}>Studio & Client Cockpit</div></div>
        </div>

        {/* mode toggle */}
        <div className="lg-el row" style={{ gap: 6, background: "var(--paper-2)", padding: 4, borderRadius: 100, border: "1px solid var(--line)", marginBottom: 22 }}>
          {(["studio", "client"] as Mode[]).map((m) => (
            <button key={m} onClick={() => { setMode(m); setErr(null); }} className="chip" style={{ flex: 1, textAlign: "center", border: "none", background: mode === m ? "var(--ink)" : "transparent", color: mode === m ? "var(--paper)" : "var(--ink-2)" }}>{m === "studio" ? "Studio team" : "Client"}</button>
          ))}
        </div>

        {mode === "studio" ? (
          <form onSubmit={submitStudio} className="lg-el col" style={{ gap: 14 }}>
            <h1 className="serif" style={{ fontSize: 26 }}>{signUp ? "Create your account" : "Welcome back"}</h1>
            {invite && (
              <div className="panel" style={{ padding: "10px 12px", background: "var(--accent-soft)", borderColor: "var(--accent)" }}>
                <div className="row" style={{ gap: 8 }}>
                  <Icon.check width={14} style={{ color: "var(--accent-ink)" }} />
                  <span style={{ fontSize: 13 }}>You're invited to {STUDIO.name} as <b>{ROLE_LABEL[invite.role] ?? invite.role}</b>. Pick a password to finish.</span>
                </div>
              </div>
            )}
            {inviteDead && (
              <div className="panel" style={{ padding: "10px 12px", borderColor: "var(--warn)" }}>
                <span style={{ fontSize: 13 }}>That invitation has already been used or withdrawn — ask the studio for a fresh link.</span>
              </div>
            )}
            {signUp && !invite && !inviteDead && (
              <div className="panel" style={{ padding: "10px 12px" }}>
                <span style={{ fontSize: 13, lineHeight: 1.5 }}>Joining the studio is by invitation — ask {STUDIO.principal} for an invite link. If you're a client, switch to <b>Client</b> above and use your access code.</span>
              </div>
            )}
            {signUp && <div className="field"><label>Name</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" /></div>}
            <div className="field"><label>Email</label><input className="input" type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@hddesignstudiogy.com" /></div>
            <div className="field"><label>Password</label><input className="input" type="password" autoComplete={signUp ? "new-password" : "current-password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={signUp ? "At least 8 characters" : "••••••••"} /></div>
            {err && <span style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</span>}
            <button className="btn accent block lg" type="submit" disabled={busy}>{busy ? "…" : signUp ? "Create account" : "Sign in"} <Icon.arrow width={16} /></button>
            <button type="button" className="link" onClick={() => { setSignUp((v) => !v); setErr(null); }} style={{ background: "none", border: "none", color: "var(--accent-ink)", fontSize: 13, cursor: "pointer" }}>
              {signUp ? "Already have an account? Sign in" : "New here? Create an account"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitClient} className="lg-el col" style={{ gap: 14 }}>
            <h1 className="serif" style={{ fontSize: 26 }}>Client sign-in</h1>
            <p className="muted" style={{ fontSize: 14 }}>Enter the access code from your studio invitation.</p>
            <div className="field"><label>Access code</label><input className="input mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Enter your access code" style={{ letterSpacing: ".12em", textTransform: "uppercase" }} autoFocus /></div>
            {err && <span style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</span>}
            <button className="btn accent block lg" type="submit" disabled={busy}>{busy ? "Checking…" : "Enter"} <Icon.arrow width={16} /></button>
          </form>
        )}
      </div>
      <div className="lg-el dim" style={{ fontSize: 12, marginTop: 20, fontStyle: "italic", zIndex: 1 }}>“{STUDIO.tagline}”</div>
    </div>
  );
}
